/**
 * Inline icons for the bulk send flow, traced from the design's own SVGs.
 * Same shape as `EditorIcons` — stroke-driven, sized by the caller, colour
 * inherited from `currentColor` unless the call site passes one.
 */

import React from "react";

interface IconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
}

const base = (size: number, color?: string, strokeWidth = 2) => ({
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: color || "currentColor",
  strokeWidth,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  style: { width: size, height: size, flexShrink: 0 },
  "aria-hidden": true,
});

export const SendIcon: React.FC<IconProps> = ({
  size = 14,
  color,
  strokeWidth,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M22 2L11 13" />
    <path d="M22 2l-7 20-4-9-9-4z" />
  </svg>
);

export const DocumentIcon: React.FC<IconProps> = ({
  size = 21,
  color,
  strokeWidth = 1.8,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 17h4" />
  </svg>
);

export const FileIcon: React.FC<IconProps> = ({
  size = 14,
  color,
  strokeWidth,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

export const UploadCloudIcon: React.FC<IconProps> = ({
  size = 21,
  color,
  strokeWidth = 1.8,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M17.5 18a4.5 4.5 0 0 0 .9-8.9A7 7 0 0 0 5 11.5 3.75 3.75 0 0 0 6.5 18z" />
    <path d="M9.5 13.5L12 11l2.5 2.5" />
    <path d="M12 11v6" />
  </svg>
);

export const DownloadIcon: React.FC<IconProps> = ({
  size = 15,
  color,
  strokeWidth,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M12 3v12" />
    <path d="M7 11l5 5 5-5" />
    <path d="M4 21h16" />
  </svg>
);

export const GridIcon: React.FC<IconProps> = ({
  size = 15,
  color,
  strokeWidth = 1.9,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 10h18M9 10v10" />
  </svg>
);

export const PeopleIcon: React.FC<IconProps> = ({
  size = 15,
  color,
  strokeWidth = 1.9,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9.5" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.9" />
  </svg>
);

export const ClockIcon: React.FC<IconProps> = ({
  size = 13,
  color,
  strokeWidth,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);

export const InfoIcon: React.FC<IconProps> = ({
  size = 15,
  color,
  strokeWidth,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M12 11v5" />
  </svg>
);

export const WarningIcon: React.FC<IconProps> = ({
  size = 15,
  color,
  strokeWidth,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

export const ShieldIcon: React.FC<IconProps> = ({
  size = 13,
  color,
  strokeWidth,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
  </svg>
);

export const CheckIcon: React.FC<IconProps> = ({
  size = 10,
  color,
  strokeWidth = 3.4,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M5 13l4 4L19 7" />
  </svg>
);

/** Points right at rest; the caller rotates it 90° to mean "expanded". */
export const ChevronRightIcon: React.FC<IconProps> = ({
  size = 11,
  color,
  strokeWidth = 2.5,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const CaretDownIcon: React.FC<IconProps> = ({
  size = 10,
  color,
  strokeWidth = 2.5,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const MailIcon: React.FC<IconProps> = ({
  size = 15,
  color,
  strokeWidth = 1.9,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

export const ShieldCheckIcon: React.FC<IconProps> = ({
  size = 17,
  color,
  strokeWidth = 1.9,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export const CalendarIcon: React.FC<IconProps> = ({
  size = 17,
  color,
  strokeWidth = 1.9,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export const TagIcon: React.FC<IconProps> = ({
  size = 17,
  color,
  strokeWidth = 1.9,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M4 7h16M4 12h11M4 17h7" />
  </svg>
);

export const EyeIcon: React.FC<IconProps> = ({
  size = 13,
  color,
  strokeWidth = 1.9,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);

export const CloseIcon: React.FC<IconProps> = ({
  size = 10,
  color,
  strokeWidth = 2.5,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const PlusIcon: React.FC<IconProps> = ({
  size = 12,
  color,
  strokeWidth = 2.5,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const RetryIcon: React.FC<IconProps> = ({
  size = 13,
  color,
  strokeWidth = 2.2,
}) => (
  <svg {...base(size, color, strokeWidth)}>
    <path d="M3 7v6h6" />
    <path d="M21 17a9 9 0 1 1-3-6.7L21 13" />
  </svg>
);
